import { Box, Chip, Typography, IconButton, Tooltip, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Roles from '../../../config/roles';

const TYPE_COLORS = {
    VACATION:     { bg: '#F0FDF4', color: '#16A34A', label: 'Vacation' },
    SICK:         { bg: '#FFFBEB', color: '#D97706', label: 'Sick' },
    COMPENSATION: { bg: '#EFF6FF', color: '#2563EB', label: 'Compensation' },
    MILITARY:     { bg: '#F8FAFC', color: '#64748B', label: 'Military' },
    CIVIL_SERVICE:{ bg: '#F8FAFC', color: '#64748B', label: 'Civil Service' },
    MATERNITY:    { bg: '#FAF5FF', color: '#7C3AED', label: 'Maternity' },
    PATERNITY:    { bg: '#FAF5FF', color: '#7C3AED', label: 'Paternity' },
    PARENTAL:     { bg: '#FAF5FF', color: '#7C3AED', label: 'Parental' },
    BEREAVEMENT:  { bg: '#F8FAFC', color: '#94A3B8', label: 'Bereavement' },
    MARRIAGE:     { bg: '#FFF7ED', color: '#EA580C', label: 'Marriage' },
    UNPAID:       { bg: '#FEF2F2', color: '#DC2626', label: 'Unpaid' },
    OTHER:        { bg: '#F8FAFC', color: '#94A3B8', label: 'Other' },
};

const STATUS_CHIP = {
    PENDING:  { label: 'Pending',  bgcolor: '#FFFBEB', color: '#D97706', border: '#FDE68A' },
    APPROVED: { label: 'Approved', bgcolor: '#F0FDF4', color: '#16A34A', border: '#BBF7D0' },
    REJECTED: { label: 'Rejected', bgcolor: '#FEF2F2', color: '#DC2626', border: '#FECACA' },
};

const colSx = { fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #EEF2F6', py: 1.5, px: 2, bgcolor: '#FAFBFC' };
const cellSx = { fontSize: 13, color: '#1E293B', borderBottom: '1px solid #F8FAFC', py: 1.5, px: 2 };

const fmt = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

export default function AbsenceTable({ absences = [], role, onApprove, onReject, onDelete, onConvertToSick, showEmployee = false }) {
    if (absences.length === 0) {
        return (
            <Box sx={{ py: 5, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>No absences recorded</Typography>
            </Box>
        );
    }

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    {showEmployee && <TableCell sx={colSx}>Employee</TableCell>}
                    <TableCell sx={colSx}>Type</TableCell>
                    <TableCell sx={colSx}>Period</TableCell>
                    <TableCell sx={colSx}>Days</TableCell>
                    <TableCell sx={colSx}>Status</TableCell>
                    <TableCell sx={colSx}>Reason</TableCell>
                    <TableCell sx={{ ...colSx, width: 48 }} />
                </TableRow>
            </TableHead>
            <TableBody>
                {absences.map(a => {
                    const type = TYPE_COLORS[a.type] || TYPE_COLORS.OTHER;
                    const status = STATUS_CHIP[a.status] || STATUS_CHIP.PENDING;
                    const period = a.startDate === a.endDate
                        ? fmt(a.startDate)
                        : `${fmt(a.startDate)} – ${fmt(a.endDate)}`;
                    const halfLabel = a.halfDay ? ` (${a.halfDayPeriod === 'MORNING' ? 'AM' : 'PM'})` : '';

                    return (
                        <TableRow key={a.id} sx={{ '&:hover': { bgcolor: '#F8FAFC' }, '&:hover .row-actions': { opacity: 1 }, '& .row-actions': { opacity: 0, transition: 'opacity 0.15s' } }}>
                            {showEmployee && (
                                <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                                    {a.employeeFirstName} {a.employeeLastName}
                                </TableCell>
                            )}
                            <TableCell sx={cellSx}>
                                <Chip size="small" label={type.label}
                                    sx={{ bgcolor: type.bg, color: type.color, fontWeight: 600, fontSize: 11, height: 20, border: 'none' }} />
                            </TableCell>
                            <TableCell sx={{ ...cellSx, color: '#64748B', whiteSpace: 'nowrap' }}>
                                {period}{halfLabel}
                            </TableCell>
                            <TableCell sx={cellSx}>{a.workingDaysCount}</TableCell>
                            <TableCell sx={cellSx}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Chip size="small" label={status.label}
                                        sx={{ bgcolor: status.bgcolor, color: status.color, border: `1px solid ${status.border}`, fontWeight: 600, fontSize: 11, height: 20 }} />
                                    {a.medicalCertificateRequired && !a.medicalCertificateUrl && (
                                        <Tooltip title="Medical certificate required">
                                            <WarningAmberIcon sx={{ fontSize: 15, color: '#F59E0B' }} />
                                        </Tooltip>
                                    )}
                                    {a.medicalCertificateUrl && (
                                        <Tooltip title="Certificate uploaded">
                                            <MedicalServicesIcon sx={{ fontSize: 15, color: '#16A34A' }} />
                                        </Tooltip>
                                    )}
                                </Box>
                            </TableCell>
                            <TableCell sx={{ ...cellSx, color: '#64748B', maxWidth: 180 }}>
                                <Typography noWrap sx={{ fontSize: 12 }}>{a.reason || '—'}</Typography>
                            </TableCell>
                            <TableCell sx={{ ...cellSx, pr: 1 }}>
                                <Box className="row-actions" sx={{ display: 'flex', gap: 0.25 }}>
                                    {role === Roles.ADMIN && a.status === 'PENDING' && (
                                        <>
                                            <Tooltip title="Approve">
                                                <IconButton size="small" onClick={() => onApprove(a)} sx={{ color: '#16A34A', '&:hover': { bgcolor: '#F0FDF4' } }}>
                                                    <CheckIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Reject">
                                                <IconButton size="small" onClick={() => onReject(a)} sx={{ color: '#DC2626', '&:hover': { bgcolor: '#FEF2F2' } }}>
                                                    <CloseIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                    {role === Roles.ADMIN && a.type === 'VACATION' && a.status === 'APPROVED' && (
                                        <Tooltip title="Convert to sick leave">
                                            <IconButton size="small" onClick={() => onConvertToSick(a)} sx={{ color: '#D97706', '&:hover': { bgcolor: '#FFFBEB' } }}>
                                                <MedicalServicesIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {a.status === 'PENDING' && (
                                        <Tooltip title="Delete">
                                            <IconButton size="small" onClick={() => onDelete(a)} sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444' } }}>
                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}
